// Based off of https://codepen.io/sandstedt/pen/JqPaKj
import * as React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { useField } from 'formik'

import { AlertText, FormLabel, HelperText } from 'library'
import styles from './styles.module.scss'

export const Checkbox = React.forwardRef(
  ({ name, className, children, disabled, ...props }, passedRef) => {
    const ref = React.useRef(null)

    return (
      <div className={classNames(styles.checkboxWrapper, className)}>
        <label
          className={classNames(styles.checkbox, {
            [styles.isDisabled]: disabled,
          })}
          htmlFor={name}
        >
          <input
            id={name}
            value={name || ''}
            disabled={disabled}
            className={styles.checkboxTrigger}
            {...props}
            type="checkbox"
            ref={(el) => {
              if (passedRef) {
                // eslint-disable-next-line no-param-reassign
                passedRef.current = el
              }
              ref.current = el
            }}
          />
          <span className={styles.checkboxSymbol}>
            <svg
              aria-hidden="true"
              className={styles.iconCheckbox}
              viewBox="0 0 28 28"
            >
              <path stroke="currentColor" d="M4 14l8 7L24 7" />
            </svg>
          </span>
          <div className={styles.checkboxTextwrapper}>{children}</div>
        </label>
      </div>
    )
  }
)

Checkbox.displayName = 'Checkbox'

Checkbox.propTypes = {
  /**
   * The Checkbox `ref` will be forwarded to the underlying element.
   * Unless the Checkbox is rendered `as` a composite component,
   * it will be a DOM node, when resolved.
   *
   * @type {ReactRef}
   * @alias ref
   */
  _ref: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  /**
   * Children should be the label of the checkbox
   */
  children: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,

  /**
   * Disabled all form inputs within the Checkbox
   */
  disabled: PropTypes.bool,
}

Checkbox.defaultProps = {
  _ref: null,
  disabled: false,
}

export function FormCheckbox({ label, ...props }) {
  const { name } = props
  const [{ onChange, onBlur, ...field }, meta] = useField(name)
  return (
    <div className={styles.checkboxContainer}>
      <Checkbox
        {...props}
        {...field}
        label={label}
        name={name}
        onBlur={onBlur(name)}
        onChange={onChange(name)}
      />
      <AlertText
        isVisible={!!(meta.touched && meta.error)}
        variant="danger"
        id={`${label}-error`}
      >
        {meta.error}
      </AlertText>
    </div>
  )
}

export function FormCheckboxGroup({
  label,
  labelDescription,
  helperText,
  children,
  isHorizontal,
  isLabelHidden,
  ...props
}) {
  return (
    <fieldset
      className={classNames(styles.checkboxGroup, {
        [styles.isHorizontal]: isHorizontal,
      })}
      {...props}
    >
      <FormLabel as="legend" srOnly={isLabelHidden}>
        {label}
      </FormLabel>
      {labelDescription && <HelperText>{labelDescription}</HelperText>}
      <div className={styles.radioButtonsWrapper}>
        {children}
        {helperText && <HelperText>{helperText}</HelperText>}
      </div>
    </fieldset>
  )
}
